// import { http } from "@/utils/http";
import { faker } from "@faker-js/faker";
import { http } from "@/utils/http";
import axios from "axios";
import { getToken } from "@/utils/auth";
import type { PublicRes } from "@/types/publicRes";
import { toQueryString } from "@/utils/live";
import { HTTP_HEADERS } from "@/config/httpHead";

export type UserResult = {
  success: boolean;
  data: {
    /** 头像 */
    avatar: string;
    /** 用户名 */
    username: string;
    /** 昵称 */
    nickname: string;
    /** 当前登录用户的角色 */
    roles: Array<string>;
    /** 按钮级别权限 */
    permissions: Array<string>;
    /** `token` */
    accessToken: string;
    /** 用于调用刷新`accessToken`的接口时所需的`token` */
    refreshToken: string;
    /** `accessToken`的过期时间（格式'xxxx/xx/xx xx:xx:xx'） */
    expires: Date;
  };
};

export type RefreshTokenResult = {
  success: boolean;
  data: {
    /** `token` */
    accessToken: string;
    /** 用于调用刷新`accessToken`的接口时所需的`token` */
    refreshToken: string;
    /** `accessToken`的过期时间（格式'xxxx/xx/xx xx:xx:xx'） */
    expires: Date;
  };
};

export type UserInfo = {
  /** 头像 */
  avatar: string;
  /** 用户名 */
  username: string;
  /** 昵称 */
  nickname: string;
  /** 邮箱 */
  email: string;
  /** 联系电话 */
  phone: string;
  /** 简介 */
  description: string;
};

export type UserInfoResult = {
  success: boolean;
  data: UserInfo;
};

// type ResultTable = { ... } // 已废弃

/** 登录 */
export const getLogin = async (data?: any) => {
  // 组装新接口参数格式
  const params = data;
  return await http.request<any>("post", "/sys/phoneLogin", {
    data: params
  });
};

/** 账号密码登陆 */
export const loginByPassword = async (data?: any) => {
  // 组装新接口参数格式
  const params = data;
  return await http.request<any>("post", "/sys/login", {
    data: params
  });
};

/** 刷新token */
export const refreshTokenApi = async () => {
  return Promise.resolve({
    success: true,
    data: {
      accessToken: faker.string.uuid(),
      refreshToken: faker.string.uuid(),
      expires: faker.date.soon().toISOString().slice(0, 19).replace("T", " ")
    }
  });
};

/** 账户设置-个人信息 */
export const getMine = async () => {
  return Promise.resolve({
    success: true,
    data: {
      avatar: faker.image.avatar(),
      username: faker.internet.userName(),
      nickname: faker.person.fullName(),
      email: faker.internet.email(),
      phone: faker.phone.number(),
      description: faker.lorem.sentence()
    }
  });
};

/** 账户设置-个人安全日志 */
export const getMineLogs = async () => {
  const list = Array.from({ length: 10 }).map((_, idx) => ({
    id: idx + 1,
    ip: faker.internet.ipv4(),
    address: faker.location.city(),
    system: faker.helpers.arrayElement(["macOS", "Windows", "Linux"]),
    browser: faker.internet.userAgent(),
    summary: faker.lorem.words(3),
    operatingTime: faker.date.recent().toISOString()
  }));
  return Promise.resolve({
    success: true,
    data: {
      list,
      total: list.length,
      pageSize: 10,
      currentPage: 1
    }
  });
};

/** 获取验证码图片 */
export const getCaptcha = async (key: string) => {
  return await http.request<any>(
    "get",
    `http://192.168.110.166:8090/sys/randomImage/${key}`
  );
};
/** 用户信息 */
export const getUserConstInfo = async (params: object) => {
  return await http.request<any>("post", `/front/user/home`, {
    data: params
  });
};

/** 更新用户信息 */
export const updateUserInfo = async (params: any) => {
  return await http.request<any>("post", "/front/user/updateProfile", {
    data: params
  });
};

/** 查询用户充值记录 */
export const getRechargeRecords = async (params: any) => {
  return await http.request<any>("post", "/front/user/recharge/records", {
    data: params
  });
};

/** 创建充值订单 */
export const createRechargeOrder = async (params: {
  packageId?: string;
  payType: "wechat" | "alipay" | string;
  orderType?: string;
  baseGrains?: number;
  countryCode?: string;
  currency?: string;
  promoCode?: string;
}) => {
  return await http.request<any>("post", "/front/user/create", {
    data: params
  });
};

/** 获取微信支付二维码（Native 扫码，PC 端使用） */
export const createQrcodeWechatUser = async (params: { orderNo: string }) => {
  return await http.request<any>("get", "/front/user/wechat/qrcode", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 获取微信 H5 支付链接（手机浏览器 MWEB） */
export const createWechatH5User = async (params: { orderNo: string }) => {
  return await http.request<any>("get", "/front/user/wechat/h5", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 获取微信支付参数（微信内置浏览器 JSAPI / H5） */
export const createWechatFormUser = async (params: { orderNo: string }) => {
  return await http.request<any>("get", "/front/user/wechat/form", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 获取支付宝支付二维码 */
export const createZfbUser = async (params: { orderNo: string }) => {
  return await http.request<any>("get", "/front/user/alipay/form", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 获取订单支付状态（0-待支付,1-支付成功,2-支付失败,3-已关闭） */
export const getPayStatus = async (params: any) => {
  return await http.request<any>("get", "/front/user/getPayStatus", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 套餐信息 */
export const getRechargePackageList = async (params: any) => {
  return await http.request<any>("get", "/recharge/packages", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 新的 套餐信息 */
export const getLivePackageList = async (params: any) => {
  return await http.get<PublicRes<any>, any>("/live-activation/packages", {
    params: {
      ...params,
      _t: new Date().getTime()
    }
  });
};

/** 获取当前生效的套餐包 还有折扣价  只有升级套餐的时候才触发 */
export const getUserLivePackage = async (params: any) => {
  const query = params ? `?${toQueryString(params)}` : "";
  return await http.get<PublicRes<any>, any>(
    `/live-activation/discount${query}`
  );
};

/** 获取系统当前的计费价格 */
export const getUserPlan = async (params: any) => {
  return await http.request<any>("get", "/front/user/getPlan", {
    data: params
  });
};

/** 上传用户头像文件 直接使用axios上传文件（避开http封装）*/
export const uploadFile = async (formData: FormData) => {
  const token = getToken();
  return await axios.post(`${HTTP_HEADERS.Http}/sys/common/upload`, formData, {
    headers: {
      Authorization: token,
      "X-Access-Token": token
    }
  });
};

/** 发送短信验证码 */
export const getSmsCode = async (data: {
  mobile: string;
  countryCode: string;
  smsmode: 1 | 2; //1注册 2登录
}) => {
  const params = data;

  return await http.request<any>("post", "/sys/sms", {
    data: params
  });
};

/** 注册 */
export const registerUser = async (data: any) => {
  const params = data;
  return await http.request<any>("post", "/sys/user/register", {
    data: params
  });
};

/** wx登陆 */
export const wxUserLogin = async (data: any) => {
  const params = data;
  return await http.request<any>("post", "/wechat/login", {
    data: params
  });
};

/** wx绑定手机号 */
export const wxUserBindPhone = async (data: any) => {
  const params = data;
  return await http.request<any>("post", "/wechat/bindPhone", {
    data: params
  });
};

/** 获取wx绑定状态 */
export const getWechatBindStatus = async (data: any) => {
  const params = data;
  return await http.request<any>("get", "/sys/getWechatBindStatus", {
    params
  });
};

/** 获取登录的图形验证码 */
export const loginRandomImage = async (key: string) => {
  return await http.request<any>("get", `/sys/randomImage/${key}`);
};
/** 用户修改密码 */
export const passwordChange = async (params: any) => {
  return await http.request<any>("get", `/sys/user/passwordChange`, {
    params
  });
};
export const phoneVerification = async (data: any) => {
  return await http.request<any>("post", `/sys/user/phoneVerification`, {
    data
  });
};

/**
 * 获取用户激活记录
 */
export const getUserActivationPage = async (params: any) => {
  return await http.request<any>(
    "get",
    `/live-user-activation/getUserActivationPage`,
    {
      params
    }
  );
};

/**
 * 获取套餐国家代码
 */
export const getCountryCodeList = async () => {
  return await http.request<any>("post", `/front/user/countryCodeList`);
};

/**
 * 校验邀请码
 */
export const checkInvitationCode = async (params: any) => {
  return await http.request<any>("get", `/sys/checkInviteCode`, {
    params
  });
};

/**
 * ticket 换票登录
 * @param data { ticket: string }
 */
export const ticketLogin = async (data: any) => {
  return await http.request<any>("post", `/domefish/dome/ticket-login`, {
    data
  });
};

/**
 * 手动绑定
 */
export const manualBind = async (data: any) => {
  return await http.request<any>("post", `/domefish/dome/complete-bind`, {
    data
  });
};
